import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Mail, Eye, EyeOff, Building2, AlertCircle, Check, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PasswordStrengthMeter, { isPasswordStrong } from "../components/PasswordStrengthMeter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OwnerLoginPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const resetToken = searchParams.get("reset");
  
  const [mode, setMode] = useState(resetToken ? "reset-password" : "login"); // login, set-password, reset-password
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ownerId, setOwnerId] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/owners/login`, {
        email: formData.email,
        password: formData.password
      }, { withCredentials: true });
      
      if (response.data.success) {
        // Check if this is an admin login
        if (response.data.is_admin) {
          toast.success("Welcome, Admin!");
          navigate("/admin");
          return;
        }
        
        // Regular owner login - Store owner ID in session
        sessionStorage.setItem("owner_id", response.data.owner_id);
        sessionStorage.setItem("owner_name", response.data.name);
        toast.success(`Welcome back, ${response.data.name}!`);
        navigate(`/dashboard?owner=${response.data.owner_id}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "Password not set. Please set up your password first.") {
        // Get owner ID from header
        const ownerIdFromHeader = err.response?.headers?.["x-owner-id"];
        if (ownerIdFromHeader) {
          setOwnerId(ownerIdFromHeader);
          setMode("set-password");
          toast.info("Please set up your password to continue");
        } else {
          // Try to find owner by email
          try {
            const ownerRes = await axios.get(`${API}/owners/by-email/${formData.email}`);
            if (ownerRes.data.found) {
              setOwnerId(ownerRes.data.owner.id);
              setMode("set-password");
              toast.info("Please set up your password to continue");
            }
          } catch {
            setError("Unable to find your account. Please contact support.");
          }
        }
      } else {
        setError(detail || "Login failed. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (!isPasswordStrong(formData.password)) {
      setError("Please create a stronger password");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await axios.post(`${API}/owners/set-password`, {
        owner_id: ownerId,
        password: formData.password,
        confirm_password: formData.confirmPassword
      });
      
      toast.success("Password set successfully! You can now log in.");
      setMode("login");
      setFormData({ ...formData, password: "", confirmPassword: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to set password");
    } finally {
      setLoading(false);
    }
  };

  // Forgot password handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/owners/forgot-password`, { email: forgotPasswordEmail });
      toast.success("If an account exists, you'll receive a reset link shortly.");
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
    } catch (err) {
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Reset password with token
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (!isPasswordStrong(formData.password)) {
      setError("Please create a stronger password");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await axios.post(`${API}/owners/reset-password`, {
        token: resetToken,
        new_password: formData.password
      });
      
      toast.success("Password reset successfully! You can now log in.");
      setSearchParams({});
      setMode("login");
      setFormData({ ...formData, password: "", confirmPassword: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-md mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-brand" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === "login" ? "Owner Login" : "Set Your Password"}
            </h1>
            <p className="text-gray-500 mt-2">
              {mode === "login" 
                ? "Access your venue dashboard" 
                : "Create a password to secure your account"}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Login Form */}
          {mode === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com or admin"
                    className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    required
                    data-testid="owner-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter your password"
                    className="w-full pl-11 pr-12 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    required
                    data-testid="owner-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3"
                data-testid="owner-login-btn"
              >
                {loading ? "Logging in..." : "Login"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setForgotPasswordEmail(formData.email);
                  setShowForgotPassword(true);
                }}
                className="w-full text-brand hover:text-brand-dark text-sm font-medium"
                data-testid="owner-forgot-password-link"
              >
                Forgot password?
              </button>
            </form>
          )}

          {/* Set Password Form */}
          {mode === "set-password" && (
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg mb-4">
                <p className="text-sm text-blue-700">
                  <strong>First time login?</strong> Create a password to access your dashboard.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create a strong password"
                    className="w-full pl-11 pr-12 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    required
                    minLength={8}
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <PasswordStrengthMeter password={formData.password} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm your password"
                    className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    required
                    minLength={6}
                    data-testid="confirm-password"
                  />
                </div>
                {formData.password && formData.confirmPassword && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${
                    formData.password === formData.confirmPassword ? "text-green-600" : "text-red-600"
                  }`}>
                    {formData.password === formData.confirmPassword ? (
                      <><Check className="w-3 h-3" /> Passwords match</>
                    ) : (
                      <><AlertCircle className="w-3 h-3" /> Passwords do not match</>
                    )}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3"
                data-testid="set-password-btn"
              >
                {loading ? "Setting password..." : "Set Password & Continue"}
              </button>

              <button
                type="button"
                onClick={() => { setMode("login"); setError(""); }}
                className="w-full text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Back to login
              </button>
            </form>
          )}

          {/* Reset Password Form (from email link) */}
          {mode === "reset-password" && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg mb-4">
                <p className="text-sm text-blue-700">
                  <strong>Reset your password</strong> - Enter a new password below
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create a strong password"
                    className="w-full pl-11 pr-12 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    required
                    minLength={8}
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <PasswordStrengthMeter password={formData.password} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm your password"
                    className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    required
                    minLength={8}
                    data-testid="reset-confirm-password"
                  />
                </div>
                {formData.password && formData.confirmPassword && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${
                    formData.password === formData.confirmPassword ? "text-green-600" : "text-red-600"
                  }`}>
                    {formData.password === formData.confirmPassword ? (
                      <><Check className="w-3 h-3" /> Passwords match</>
                    ) : (
                      <><AlertCircle className="w-3 h-3" /> Passwords do not match</>
                    )}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !isPasswordStrong(formData.password) || formData.password !== formData.confirmPassword}
                className="w-full btn-primary py-3 disabled:opacity-50"
                data-testid="reset-password-btn"
              >
                {loading ? "Resetting password..." : "Reset Password"}
              </button>

              <button
                type="button"
                onClick={() => { setMode("login"); setSearchParams({}); setError(""); }}
                className="w-full text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Back to login
              </button>
            </form>
          )}

          {/* Footer Links */}
          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500">
              Don't have an account?{" "}
              <button
                onClick={() => navigate("/claim")}
                className="text-brand hover:text-brand-dark font-medium"
              >
                Claim your venue
              </button>
            </p>
          </div>
        </motion.div>
      </div>

      <Footer />

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgotPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowForgotPassword(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Forgot Password</h3>
                <button onClick={() => setShowForgotPassword(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-gray-600 text-sm mb-4">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20"
                      required
                      data-testid="owner-forgot-email"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !forgotPasswordEmail.trim()}
                  className="w-full px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-50"
                  data-testid="owner-send-reset-btn"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
